<template>
    <form-fieldset :error="error">
        <input :id="id" type="hidden" :name="name">
        <trix-editor
            :input="id"
            class="form-rich-editor"
            :class="{ error }"
            @trix-change="onChange"
            @trix-file-accept.prevent=""
            @trix-attachment-add="event => event.attachment.remove()"
            @trix-focus="$emit('focus')"
        />
    </form-fieldset>
</template>
<script>
import Trix from 'trix';
import 'trix/dist/trix.css';
import { isString } from 'lodash';

import FormFieldset from './FormFieldset';
import { toolbarCallback } from '@/utils/trix';

export default {
    components: {
        FormFieldset,
    },

    props: {
        value: {
            type: String,
            default: '',
        },

        id: {
            required: true,
            type: String,
        },

        name: {
            required: true,
            type: String,
        },

        error: {
            type: Boolean,
            default: false,
        },
    },

    data: () => ({
        element: null,
        editor: null,
        isLocked: false,
    }),

    watch: {
        /**
         * Handles change of value outside of component.
         *
         * @param  {string} value Content value.
         */
        value(value) {
            if (this.isLocked || ! isString(value)) {
                return;
            }

            this.lockContentRefresh(true);
            this.editor.loadHTML(value);
            this.lockContentRefresh(false);
        },
    },

    /**
     * Creates component.
     */
    created() {
        document.addEventListener('trix-before-initialize', this.setupTrix());
    },

    /**
     * Mounts components.
     */
    mounted() {
        this.element = this.$el.querySelector('trix-editor');
        this.editor = this.element.editor;

        if (! isString(this.value)) {
            return;
        }

        this.lockContentRefresh(true);
        this.editor.loadHTML(this.value);
        this.lockContentRefresh(false);
    },

    methods: {
        /**
         * Set-ups Trix configuration.
         */
        setupTrix() {
            // Block attributes
            delete Trix.config.blockAttributes.attachmentGallery;
            delete Trix.config.blockAttributes.bullet;
            delete Trix.config.blockAttributes.bulletList;
            delete Trix.config.blockAttributes.code;
            delete Trix.config.blockAttributes.heading1;
            delete Trix.config.blockAttributes.number;
            delete Trix.config.blockAttributes.numberList;
            delete Trix.config.blockAttributes.quote;

            Trix.config.blockAttributes.paragraph = {
                tagName: 'p',
                parse: false,
            };

            // Text attributes
            delete Trix.config.textAttributes.strike;

            Trix.config.textAttributes.underline = {
                style: { textDecoration: 'underline' },
                inheritable: true,
                parser: (element) => 'underline' === window.getComputedStyle(element).textDecoration,
            };

            // Languages
            Trix.config.lang.underline = 'Underline';

            // Toolbar
            Trix.config.toolbar.getDefaultHTML = toolbarCallback;
        },

        /**
         * Handles change event.
         *
         * @param  {Event} event Change event.
         */
        onChange(event) {
            if (this.isLocked) {
                return;
            }

            this.lockContentRefresh(true);
            this.$emit('input', event.target.value ?? event.target.innerHTML);
            this.$nextTick(() => {
                this.lockContentRefresh(false);
            });
        },

        /**
         * Locks or unlocks content refresh events.
         *
         * @param  {boolean} status Lock status.
         */
        lockContentRefresh(status) {
            this.isLocked = status;
        },
    },
};
</script>
<style>
trix-toolbar .trix-button--icon-underline::before {
    height: 21px;
    top: 2px;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'%3E%3Cpath d='M0 0h24v24H0z' fill='none'/%3E%3Cpath d='M12 17c3.31 0 6-2.69 6-6V3h-2.5v8c0 1.93-1.57 3.5-3.5 3.5S8.5 12.93 8.5 11V3H6v8c0 3.31 2.69 6 6 6zm-7 2v2h14v-2H5z'/%3E%3C/svg%3E");
}
</style>
